import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';

const HERO_BG = 'https://images.unsplash.com/photo-1638864616275-9f0b291a2eb6?crop=entropy&cs=srgb&fm=jpg&q=85';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.1 + i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-screen w-full overflow-hidden pt-44">
      {/* Background */}
      <div
        className="absolute inset-0 z-0 opacity-30"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/40 via-black/70 to-black" />

      {/* Grid lines */}
      <div className="absolute inset-0 z-0 opacity-[0.07]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-24">
        {/* Top meta */}
        <motion.div
          variants={fadeUp} initial="hidden" animate="show" custom={0}
          className="flex items-center gap-3 mb-12"
        >
          <span className="w-1.5 h-1.5 bg-brand-primary animate-pulse-dot" />
          <span className="font-manrope text-xs uppercase tracking-[0.3em] text-white/60">
            Digital Solutions Agency
          </span>
        </motion.div>

        {/* Massive headline */}
        <motion.h1
          variants={fadeUp} initial="hidden" animate="show" custom={1}
          className="font-outfit font-black tracking-tighter leading-[0.85] text-white text-[18vw] sm:text-[16vw] lg:text-[13vw] xl:text-[12rem]"
          data-testid="hero-headline"
        >
          We <span className="italic font-light text-brand-primary">elevate</span>
        </motion.h1>
        <motion.h1
          variants={fadeUp} initial="hidden" animate="show" custom={2}
          className="font-outfit font-black tracking-tighter leading-[0.85] text-white text-[18vw] sm:text-[16vw] lg:text-[13vw] xl:text-[12rem] -mt-2"
        >
          businesses.
        </motion.h1>

        {/* Sub & CTAs */}
        <div className="mt-12 grid lg:grid-cols-12 gap-10 items-end">
          <motion.p
            variants={fadeUp} initial="hidden" animate="show" custom={3}
            className="font-manrope text-base sm:text-lg text-white/70 leading-relaxed lg:col-span-6 max-w-xl"
            data-testid="hero-subtitle"
          >
            ELEVA Digital is the partner you call when growth gets serious.
            Web & app development, marketing, AI, automation, and IT consulting —
            engineered for measurable results.
          </motion.p>

          <motion.div
            variants={fadeUp} initial="hidden" animate="show" custom={4}
            className="lg:col-span-6 flex flex-wrap gap-4 lg:justify-end"
          >
            <a
              href="mailto:info@elevadigital.com.au?subject=Project%20Inquiry"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center gap-3 bg-brand-primary text-black font-manrope font-semibold uppercase tracking-wider text-sm px-8 py-5 hover:bg-white transition-colors duration-300"
            >
              Start a Project
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#services"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-3 bg-transparent border border-white/20 text-white font-manrope font-semibold uppercase tracking-wider text-sm px-8 py-5 hover:bg-white hover:text-black transition-colors duration-300"
            >
              Explore Services
            </a>
          </motion.div>
        </div>

        {/* Stats bar */}
        <motion.div
          variants={fadeUp} initial="hidden" animate="show" custom={5}
          className="mt-24 border-t border-white/10 pt-8 grid grid-cols-2 lg:grid-cols-4 gap-8"
          data-testid="hero-stats"
        >
          {[
            { value: '120+', label: 'Projects Shipped' },
            { value: '40+', label: 'Industries Served' },
            { value: '98%', label: 'Client Retention' },
            { value: '24/7', label: 'Engineering Pulse' },
          ].map((stat) => (
            <div key={stat.label} className="flex flex-col gap-1">
              <span className="font-outfit font-black text-4xl lg:text-5xl text-white tracking-tighter">
                {stat.value}
              </span>
              <span className="font-manrope text-xs uppercase tracking-[0.2em] text-white/50">
                {stat.label}
              </span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
